// maps.js
import React, { useEffect, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Circle,
  useMapEvents,
  Popup,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// 📏 Haversine formula to calculate distance between two coordinates
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371; // Earth radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // in km
};

// 📍 Dummy players (static sample data)
const dummyPlayers = [
  {
    id: 1,
    name: "Person A (2km)",
    lat: 10.429231,
    lng: 123.912075,
  },
  {
    id: 2,
    name: "Person B (6km)",
    lat: 10.410000,
    lng: 123.885000,
  },
  {
    id: 3,
    name: "Person C (10km)",
    lat: 10.390000,
    lng: 123.950000,
  },
];

// 📍 Right-click handler
const RightClickHandler = ({ onRightClick }) => {
  useMapEvents({
    contextmenu(e) {
      onRightClick(e.latlng);
    },
  });
  return null;
};

const Maps = () => {
  const [userLocation, setUserLocation] = useState(null);
  const [radius, setRadius] = useState(50000); // meters
  const [clickedCoords, setClickedCoords] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      () => {
        alert("📍 GPS failed. Using fallback.");
        setUserLocation({ lat: 10.450464, lng: 123.895826 }); // Your default fallback
      },
      { enableHighAccuracy: true }
    );
  }, []);

  const handleCopy = (lat, lng) => {
    const coordString = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    navigator.clipboard.writeText(coordString).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  if (!userLocation) return <div className="p-3">📡 Locating...</div>;

  return (
    <div style={{ height: "100vh", width: "100%" }}>
      {/* Radius Slider */}
      <div
        style={{
          position: "absolute",
          top: 20,
          left: 20,
          zIndex: 1000,
          background: "#fff",
          padding: "12px 16px",
          borderRadius: "8px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <label>
          Radius: {(radius / 1000).toFixed(1)} km
          <input
            type="range"
            min="0"
            max="50000"
            value={radius}
            onChange={(e) => setRadius(+e.target.value)}
            style={{ width: "200px", marginLeft: "10px" }}
          />
        </label>
      </div>

      {/* Copied notification */}
      {copied && (
        <div
          style={{
            position: "absolute",
            top: 80,
            left: 20,
            background: "#28a745",
            color: "#fff",
            padding: "6px 12px",
            borderRadius: "4px",
            zIndex: 1000,
            boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
          }}
        >
          ✅ Coordinates copied!
        </div>
      )}

      {/* Map */}
      <MapContainer
        center={[userLocation.lat, userLocation.lng]}
        zoom={13}
        style={{ height: "100%", width: "100%" }}
        contextmenu={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Right-click handler */}
        <RightClickHandler onRightClick={setClickedCoords} />

        {/* User marker & radius circle */}
        <Marker position={[userLocation.lat, userLocation.lng]}>
          <Popup>📍 You are here</Popup>
        </Marker>
        <Circle
          center={[userLocation.lat, userLocation.lng]}
          radius={radius}
          pathOptions={{
            color: "blue",
            fillColor: "skyblue",
            fillOpacity: 0.3,
          }}
        />

        {/* Dummy players within radius */}
        {dummyPlayers.map((player) => {
          const distance = calculateDistance(
            userLocation.lat,
            userLocation.lng,
            player.lat,
            player.lng
          );

          if (distance * 1000 <= radius) {
            return (
              <Marker key={player.id} position={[player.lat, player.lng]}>
                <Popup>
                  {player.name}
                  <br />
                  🧭 {distance.toFixed(2)} km away
                </Popup>
              </Marker>
            );
          }
          return null;
        })}

        {/* Right-click coordinate popup */}
        {clickedCoords && (
          <Popup
            position={[clickedCoords.lat, clickedCoords.lng]}
            eventHandlers={{
              click: () =>
                handleCopy(clickedCoords.lat, clickedCoords.lng),
            }}
          >
            <div style={{ cursor: "pointer" }}>
              📋 Copy:
              <br />
              <strong>
                {clickedCoords.lat.toFixed(6)},{" "}
                {clickedCoords.lng.toFixed(6)}
              </strong>
              <br />
              <small>(Click to copy)</small>
            </div>
          </Popup>
        )}
      </MapContainer>
    </div>
  );
};

export default Maps;
